import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { X, Pause, Play } from "lucide-react";
import useEmblaCarousel from "embla-carousel-react";
import Autoplay from "embla-carousel-autoplay";
import { trackAdClick } from "@/hooks/useAdTracking";
import { useAuth } from "@/hooks/useAuth";

interface Advertisement {
  id: string;
  title: string;
  description: string;
  image_url?: string;
  link_url?: string;
}

export const HeroAdvertisement = () => {
  const [ads, setAds] = useState<Advertisement[]>([]);
  const [isVisible, setIsVisible] = useState(true);
  const [isPlaying, setIsPlaying] = useState(true);
  const { user } = useAuth();
  const navigate = useNavigate();
  const autoplayPlugin = useCallback(() => Autoplay({ delay: 5000, stopOnInteraction: false }), []);
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true }, [autoplayPlugin()]);

  useEffect(() => {
    fetchHeroAds();

    const channel = supabase
      .channel('hero-advertisements')
      .on('postgres_changes', 
        { event: '*', schema: 'public', table: 'advertisements' },
        () => fetchHeroAds()
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const fetchHeroAds = async () => {
    const now = new Date().toISOString();
    const { data, error } = await supabase
      .from('advertisements')
      .select('*')
      .eq('active', true)
      .eq('position', 'hero')
      .lte('start_date', now)
      .or(`end_date.is.null,end_date.gte.${now}`)
      .order('created_at', { ascending: false });

    if (!error && data && data.length > 0) {
      setAds(data);
      setIsVisible(true);
    } else {
      setAds([]);
    }
  };

  const toggleAutoplay = useCallback(() => {
    if (!emblaApi) return;
    const autoplay = emblaApi.plugins()?.autoplay;
    if (!autoplay) return;

    if (isPlaying) {
      autoplay.stop();
    } else {
      autoplay.play();
    }
    setIsPlaying(!isPlaying);
  }, [emblaApi, isPlaying]);

  if (ads.length === 0 || !isVisible) return null;

  return (
    <div className="relative overflow-hidden bg-background">
      <div className="overflow-hidden" ref={emblaRef}>
        <div className="flex">
          {ads.map((ad) => (
            <div key={ad.id} className="flex-[0_0_100%] min-w-0">
              <div 
                className="relative w-full cursor-pointer group"
                onClick={() => {
                  trackAdClick(ad.id, user?.id);
                  if (!ad.link_url) {
                    navigate(`/promo/${ad.id}`);
                  } else if (ad.link_url.startsWith('/')) {
                    navigate(ad.link_url);
                  } else {
                    window.location.href = ad.link_url;
                  }
                }}
              >
                {ad.image_url && (
                  <div className="w-full aspect-video overflow-hidden bg-slate-900 flex items-center justify-center">
                    <img
                      src={ad.image_url}
                      alt={ad.title}
                      className="w-full h-[85%] object-contain"
                    />
                  </div>
                )}
                
                {/* Overlay with text for accessibility */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent flex items-end">
                  <div className="container py-6 px-4">
                    <div className="max-w-2xl">
                      <h3 className="text-2xl md:text-4xl font-bold text-white mb-2 drop-shadow-lg">
                        {ad.title}
                      </h3>
                      {ad.description && (
                        <p className="text-base md:text-lg text-white/90 drop-shadow-md">
                          {ad.description}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {/* Close button */}
      <button
        onClick={() => setIsVisible(false)}
        className="absolute top-4 right-4 z-10 p-2 bg-black/30 hover:bg-black/50 backdrop-blur-sm rounded-full transition-colors"
        aria-label="Fermer"
      >
        <X className="h-5 w-5 text-white" />
      </button>
      {/* Navigation dots */}
      {ads.length > 1 && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2 z-10">
          {ads.map((_, index) => (
            <button
              key={index}
              onClick={() => emblaApi?.scrollTo(index)}
              className={`w-2 h-2 rounded-full transition-all ${
                index === 0 ? 'bg-white w-8' : 'bg-white/50 hover:bg-white/75'
              }`}
              aria-label={`Aller à la publicité ${index + 1}`}
            />
          ))}
        </div>
      )}
      
      {/* Pause/Play button */}
      {ads.length > 1 && (
        <button
          onClick={toggleAutoplay}
          className="absolute top-4 right-16 bg-black/30 hover:bg-black/50 backdrop-blur-sm p-2 rounded-full transition-colors z-10"
          aria-label={isPlaying ? "Pause" : "Lecture"}
        >
          {isPlaying ? <Pause className="h-4 w-4 text-white" /> : <Play className="h-4 w-4 text-white" />}
        </button>
      )}
    </div>
  );
};
